//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import { z } from 'zod';

export const PROVIDERS = [
  'openai',
  'claude',
  'gemini',
  'ibm-watson-x',
  'azure-openai',
  'azure-openai-gateway',
  'ollama',
] as const;

export type Provider = (typeof PROVIDERS)[number];

/**
 * Return the subset of providers available given the runtime config.
 * Ollama is hidden unless `services.ollamaProvider.show` is true — it is a
 * self-hosted provider that requires a running Ollama instance and is
 * disabled by default in all standard deployments.
 */
export function getAvailableProviders(
  services?: { ollamaProvider?: { show?: boolean } } | null,
): Provider[] {
  const ollamaEnabled = services?.ollamaProvider?.show === true;
  return PROVIDERS.filter((p) => p !== 'ollama' || ollamaEnabled);
}

// Providers with a live reasoning-model toggle (surfaced in Task 2's Advanced section).
export const REASONING_MODEL_PROVIDERS = new Set<Provider>([
  'openai',
  'claude',
  'gemini',
]);

const nameSchema = z
  .string()
  .trim()
  .min(1, 'name-required')
  .regex(/^[a-zA-Z0-9 _-]*$/, 'specialCharactersNotAllowed');

const reasoningFields = {
  reasoningEnabled: z.boolean().optional(),
  reasoningEffort: z.enum(['low', 'medium', 'high', 'xhigh']).optional(),
  thinkingBudget: z.number().optional(),
  thinkingLevel: z.enum(['low', 'medium', 'high']).optional(),
};

// Base fields shared by openai, claude, and gemini providers
const baseLlmFields = {
  name: nameSchema,
  apiKey: z.string().trim().min(1, 'api-key-required'),
  model: z.string().trim().min(1, 'model-required'),
  apiEndpoint: z.string().trim().optional(),
  ...reasoningFields,
};

const openaiSchema = z.object({
  provider: z.literal('openai'),
  ...baseLlmFields,
});

const claudeSchema = z.object({
  provider: z.literal('claude'),
  ...baseLlmFields,
});

const geminiSchema = z.object({
  provider: z.literal('gemini'),
  ...baseLlmFields,
});

const watsonxSchema = z.object({
  provider: z.literal('ibm-watson-x'),
  name: nameSchema,
  apiKey: z.string().trim().min(1, 'api-key-required'),
  apiEndpoint: z.string().trim().min(1, 'api-endpoint-required'),
  projectId: z.string().trim().min(1, 'project-id-required'),
  model: z.string().trim().min(1, 'model-required'),
});

// azure-openai and azure-openai-gateway share every field except the literal
// discriminant — discriminatedUnion requires each branch keyed by its own
// literal, so the shared fields are spread into two schemas rather than one
// z.enum(['azure-openai', 'azure-openai-gateway']) branch.
const azureFields = {
  name: nameSchema,
  apiKey: z.string().trim().min(1, 'api-key-required'),
  apiEndpoint: z.string().trim().min(1, 'api-endpoint-required'),
  azureOpenAIApiDeploymentName: z
    .string()
    .trim()
    .min(1, 'deployment-name-required'),
  azureOpenAIApiVersion: z.string().trim().min(1, 'api-version-required'),
  azureOpenAIEmbeddingDeploymentName: z.string().optional(),
};

const azureOpenAiSchema = z.object({
  provider: z.literal('azure-openai'),
  ...azureFields,
});

const azureOpenAiGatewaySchema = z.object({
  provider: z.literal('azure-openai-gateway'),
  ...azureFields,
});

const ollamaSchema = z.object({
  provider: z.literal('ollama'),
  name: nameSchema,
  apiEndpoint: z.string().trim().min(1, 'api-endpoint-required'),
  model: z.string().trim().min(1, 'model-required'),
  outputCeiling: z.preprocess(
    (v) =>
      v === null || (typeof v === 'string' && v.trim() === '') ? undefined : v,
    z.coerce
      .number({ invalid_type_error: 'output-ceiling-invalid' })
      .int('output-ceiling-invalid')
      .positive('output-ceiling-invalid')
      .optional(),
  ),
  // Gate for whether `reasoning` is sent at all. Every other reasoning-model
  // provider (openai, claude, gemini) has a `reasoningEnabled` gate; without
  // this, Ollama's freeform field is the only reasoning value with no way
  // to turn it off.
  reasoningEnabled: z.boolean().optional(),
  reasoning: z
    .string()
    .trim()
    .transform((v) => (v === '' ? undefined : v))
    .optional(),
});

export const integrationSchema = z
  .discriminatedUnion('provider', [
    openaiSchema,
    claudeSchema,
    geminiSchema,
    watsonxSchema,
    azureOpenAiSchema,
    azureOpenAiGatewaySchema,
    ollamaSchema,
  ])
  .superRefine((data, ctx) => {
    // Custom mode with a blank value is a silent no-op (reasoningEnabled
    // stays true, reasoning transforms to undefined) — block it instead of
    // letting the save "succeed" with nothing actually overridden.
    if (
      data.provider === 'ollama' &&
      data.reasoningEnabled &&
      !data.reasoning
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reasoning'],
        message: 'reasoning-required-when-custom',
      });
    }
  })
  .transform((data) => {
    // Strip a stale `reasoning` value whenever it's not gated on: covers
    // both switching Custom -> Automatic (react-hook-form keeps the field's
    // last value in state since shouldUnregister isn't set) and a legacy
    // record with `reasoning` set but `reasoningEnabled` falsy/absent.
    // Without this, "Automatic" is shown while a stale override keeps being
    // resubmitted on every save (PR #1012 review).
    if (data.provider === 'ollama' && !data.reasoningEnabled) {
      return { ...data, reasoning: undefined };
    }
    return data;
  });

export type IntegrationFormValues = z.infer<typeof integrationSchema>;

/** Empty-but-valid-shape defaults for a fresh provider selection. Fields are
 * intentionally blank (not pre-filled with fake values) so validation still
 * blocks submission until the user fills them in. */
export function defaultValuesForProvider(
  provider: Provider,
  name = '',
): IntegrationFormValues {
  switch (provider) {
    case 'openai':
    case 'claude':
    case 'gemini': {
      const base = {
        name,
        apiKey: '',
        model: '',
        apiEndpoint: '',
        // Default a fresh integration to reasoning enabled (NEXUS00007282,
        // interim ahead of the full "Automatic by default" redesign): these
        // are agentic ReAct loops, and a non-reasoning-capable turn produces
        // confident wrong tool calls rather than a merely worse answer. Only
        // changes behavior for OpenAI/Gemini (unset -> off there today);
        // Claude's _build_anthropic already coerces unset -> True on the
        // backend, so this just makes that explicit in stored data rather
        // than changing behavior. Does not touch any existing integration —
        // this is the create-mode default only, never read on edit.
        reasoningEnabled: true,
      };
      if (provider === 'claude') {
        return { ...base, provider, thinkingBudget: 10000 };
      }
      if (provider === 'gemini') {
        return { ...base, provider, thinkingLevel: 'medium' };
      }
      return { ...base, provider, reasoningEffort: 'medium' };
    }
    case 'ibm-watson-x':
      return {
        provider,
        name,
        apiKey: '',
        apiEndpoint: '',
        projectId: '',
        model: '',
      };
    case 'azure-openai':
    case 'azure-openai-gateway':
      return {
        provider,
        name,
        apiKey: '',
        apiEndpoint: '',
        azureOpenAIApiDeploymentName: '',
        azureOpenAIApiVersion: '',
      };
    case 'ollama':
      return { provider, name, apiEndpoint: '', model: '' };
    default:
      // Unknown/unrecognized provider string (e.g. stale or malformed
      // initialData.provider after normalization) — fall back to the
      // openai shape rather than returning undefined, which would leave
      // the form with no provider tab selected and no visible feedback.
      return defaultValuesForProvider('openai', name);
  }
}
